import React, { useEffect, useState } from "react";
import { Button, Space } from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import ChargeConfigDrawer from "./ChargeConfigDrawer";
import useChargeConfigs from "hooks/finance/useChargeConfigs";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import ChargeConfigsTable from "./ChargeConfigsTable";
import ChargeViewDrawer from "./ChargeViewDrawer";
import EffectiveChargePreviewDrawer from "./EffectiveChargePreviewDrawer";

// enums
export const APP_STAGES = [
  "OLITASK_MOBILE_UI_ALL",
  "OLITASK_MOBILE_UI_IN_PROGRESS",
  "OLITASK_MOBILE_UI_NEW",
  "OLITASK_MOBILE_UI_COMPLETED",
  "OLITASK_MOBILE_UI_ARRIVED",
  "OLITASK_MOBILE_UI_OTW",
  "OLITASK_MOBILE_UI_ACCEPTED",
];

export const CHARGE_CATEGORIES = [
  { value: "FEE", label: "Fee" },
  { value: "TAX", label: "Tax" },
  { value: "SURCHARGE", label: "Surcharge" },
  { value: "DISCOUNT", label: "Discount" },
];

export const CHARGE_STATUS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

export const CURRENCIES = [
  { value: "USD", label: "USD" },
  { value: "UGX", label: "UGX" },
];

export const CHARGE_SCOPES = [
  "COUNTRY",
  "FEDERAL",
  "STATE",
  "COUNTY",
  "CITY",
  "OLIVEEB",
  "GLOBAL",
  "OTHER",
];

export const OLIVEEB_APPS = [
  "OLITASK_MOBILE_UI",
  "OLITASK_WEB_UI",
  "OLIVEEB_ADMIN_PORTAL_WEB_UI",
  "OLITASK_MAIN_SERVICE",
  "OLIVEEB_GATEWAY_SERVICE",
  "OLIVEEB_IDENTITY_SERVICE",
  "OLIVEEB_PROFILE_SERVICE",
  "OLIVEEB_PAYMENT_SERVICE",
];

export const CHARGE_ITEM_CODES = [
  // OLITASK — TASK PLATFORM
  "OLITASK_SERVICE_FEE",
  "OLITASK_TASK_PAYMENT",
  "OLITASK_HELP_REQUEST",
  "OLITASK_APP_SUBSCRIPTION",
  "OLITASK_DONATION",
  "OLITASK_HELP_BASE",
  "OLITASK_HELP_TIP",
  "OLITASK_TASK_INSURANCE",
  "OLITASK_PRIORITY_SUPPORT",
  "OLITASK_CANCELLATION_FEE",
  "OLITASK_LATE_PAYMENT_FEE",
  "OLITASK_ESCROW_HOLD_FEE",
  "OLITASK_ESCROW_RELEASE_FEE",
  "OLITASK_PAYOUT_PROCESSING",
  "OLITASK_STRIPE_CONNECT_FEE",
  "OLITASK_PROMOTION_CREDIT",
  "OLITASK_COUPON_REIMBURSE",

  // PLATFORM-WIDE / OLIVEEB
  "OLIVEEB_PLATFORM_FEE",
  "OLIVEEB_ACCOUNT_VERIFICATION",
  "OLIVEEB_IDENTITY_VERIFICATION",
  "OLIVEEB_FRAUD_PROTECTION",
  "OLIVEEB_DISPUTE_REVIEW",
  "OLIVEEB_API_USAGE_FEE",
  "OLIVEEB_TRANSFER_SERVICE",
  "OLIVEEB_REFUND_ADJUSTMENT",
  "OLIVEEB_CHARGEBACK_FEE",

  // OLESCROW — ESCROW
  "OLESCROW_CREATE",
  "OLESCROW_HOLD",
  "OLESCROW_RELEASE",
  "OLESCROW_CANCELLATION",
  "OLESCROW_DISPUTE_FEE",
  "OLESCROW_ARBITRATION",
  "OLESCROW_LATE_RELEASE_FEE",
  "OLESCROW_INTEREST_EARNED",
  "OLESCROW_ASSET_TRANSFER",

  // BNW — Marketplace / Logistics
  "BNW_SERVICE",
  "BNW_PRODUCT_SALE",
  "BNW_LISTING_FEE",
  "BNW_SELLER_COMMISSION",
  "BNW_FULFILLMENT_FEE",
  "BNW_LOGISTICS_FEE",
  "BNW_PREMIUM_SELLER_SUBSCRIPTION",
  "BNW_RETURN_PROCESSING",
  "BNW_FRAUD_GUARANTEE",

  // FOREX — FX & TRANSFERS
  "FOREX_CONVERSION_FEE",
  "FOREX_TRANSFER_FEE",
  "FOREX_SPREAD_MARKUP",
  "FOREX_LIQUIDITY_FEE",
  "FOREX_SETTLEMENT_FEE",
  "FOREX_COMPLIANCE_FEE",
  "FOREX_DIRECTIONAL_HEDGE_FEE",
];

export default function ChargeConfigsPage() {
  const {
    charges,
    loading,
    totalElements,
    fetchAllCharges,
    fetchEffectiveCharges,
    createCharge,
    updateCharge,
    toggleChargeStatus,
  } = useChargeConfigs();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [filters, setFilters] = useState({
    app: "OLITASK_MOBILE_UI",
    stages: [
      "OLITASK_MOBILE_UI_ALL",
      "OLITASK_MOBILE_UI_IN_PROGRESS",
      "OLITASK_MOBILE_UI_NEW",
      "OLITASK_MOBILE_UI_ACCEPTED",
      "OLITASK_MOBILE_UI_OTW",
      "OLITASK_MOBILE_UI_COMPLETED",
      "OLITASK_MOBILE_UI_ARRIVED",
    ],
    currency: "USD",
    category: null,
    status: null,
    country: "US",
    code: "",
  });
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingCharge, setViewingCharge] = useState(null);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // drawer handlers
  const handleView = (record) => {
    setViewingCharge(record);
    setViewDrawerOpen(true);
  };

  const handleCreate = () => {
    setEditingCharge(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCharge(record);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload) => {
    if (editingCharge) {
      await updateCharge(editingCharge.id, payload);
    } else {
      await createCharge(payload);
    }
    fetchAllCharges(pagination.current - 1, pagination.pageSize);
    setDrawerOpen(false);
  };

  const handlePreviewEffective = async () => {
    await fetchEffectiveCharges({
      ...filters,
      page: 0,
      size: 50,
    });

    setPreviewDrawerOpen(true);
  };

  const handleToggleStatus = async (record) => {
    const newStatus = record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    await toggleChargeStatus(record.id, newStatus);
  };

  const handlePageChange = (pag) => {
    setPagination(pag);
    fetchAllCharges(pag.current - 1, pag.pageSize);
  };

  const paginationConfig = {
    ...pagination,
    total: totalElements,
    showSizeChanger: true,
    showTotal: (total) => `Total ${total} charges`,
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="envelope-open-text"
          title="Charge Configurations"
          subtitle="Manage all payment charge configurations"
          className="mb-3 mt-3 flex-1"
        />

        <Space>
          <Button icon={<EyeOutlined />} onClick={() => setPreviewDrawerOpen(true)}>
            Effective Charges
          </Button>

          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Charge
          </Button>
        </Space>
      </div>

      <ChargeConfigsTable
        charges={charges}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        pagination={paginationConfig}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
        onView={handleView}
        onToggleStatus={handleToggleStatus}
      />

      {/* create / edit drawer */}
      <ChargeConfigDrawer
        open={drawerOpen}
        charge={editingCharge}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
      />

      <ChargeViewDrawer
        charge={viewingCharge}
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
      />

      <EffectiveChargePreviewDrawer
        open={previewDrawerOpen}
        onClose={() => setPreviewDrawerOpen(false)}
      />
    </>
  );
}
